/**
 * RequirementReadinessService — V96-1-7 / ADR-0044 §4.
 *
 * Framework readiness derived through the Common Control Graph:
 *   evidence (control_checks on implementation controls)
 *     → reviewed crediting edges (control → requirement)
 *       → requirement status → framework counts.
 *
 * Nothing here is stored; it is recomputed from evidence so a stale check
 * can never leave a requirement looking met. Rules:
 *   - Only edges with a reviewer credit a requirement. `references` edges
 *     (KSI → 800-53 citations) never credit.
 *   - met      — every applicable crediting control passes with fresh
 *                evidence (V102-0-2: AND, not "any full control"). A
 *                mapped control that fails is an exception against the
 *                requirement however it is related. Not-applicable
 *                controls are left out; all-N/A counts as met.
 *   - partial  — some crediting controls pass, not enough for met.
 *   - gap      — crediting controls exist; none pass (fail, stale, excepted,
 *                or no evidence yet — the reason says which).
 *   - unmapped — no reviewed crediting edge. Counted against readiness,
 *                never as met, and reported separately as coverage.
 *
 * V108-3-5 / ADR-0085 §5: only evidence from a COUNTED environment moves a
 * control state — production plus the environments the program's boundary
 * explicitly includes (`countedEnvironments`). Checks from any other
 * environment are returned as a `preview` with the reason, never as state.
 */
import { SECURITY_BASELINE_CONTROLS } from '../data/security-baseline.js';
import { queryInChunks, sqlIn } from '../utils/sql-in.js';
import { applyEvidenceJudgment } from './control-evidence-judgment.js';
import { countedResultSql } from '../rules/preview.js';
import { appliesToClass } from '../utils/class-applicability.js';
import { couldntCheckEntry, defaultCouldntCheckReason, isCouldntCheckReason } from '../enablers/couldnt-check.js';
import { isAgedOutPass, nextCollectionAt, openAcceptancesByItem } from './readiness-support.js';
import { connectorEnvironments, countedEnvironments as resolveCountedEnvironments, countedEnvironmentSet, previewReason, } from './customer-environment-service.js';
import { judgeBinding } from './binding-judgment.js';
import { ciAssetReviewStatus, outOfScopeItem } from './configuration-item-grace-service.js';
const EVIDENCE_MODE = new Map(SECURITY_BASELINE_CONTROLS.map((c) => [c.id, c.mode]));
export function isCouldntCheckState(s) {
    return s === 'couldnt_check' || s === 'stale' || s === 'insufficient';
}
// V109-1-19 R3: the shared freshness / schedule / acceptance rules live in readiness-support.ts (the
// objective engine uses them too and may not import this crediting layer); re-exported here.
export { isAgedOutPass, loadControlFreshness, nextCollectionAt, openAcceptancesByItem } from './readiness-support.js';
export const REQUIREMENT_CHECK_STATES = ['pass', 'fail', 'couldnt_check', 'excepted', 'no_evidence', 'not_applicable', 'unmapped'];
export function requirementCheckState(status, crediting) {
    if (status === 'unmapped')
        return 'unmapped';
    const applicable = crediting.filter((c) => c.state !== 'not_applicable');
    if (!applicable.length)
        return 'not_applicable';
    if (status === 'met')
        return 'pass';
    if (applicable.some((c) => c.state === 'fail'))
        return 'fail';
    if (applicable.some((c) => isCouldntCheckState(c.state)))
        return 'couldnt_check';
    if (applicable.some((c) => c.state === 'excepted'))
        return 'excepted';
    return 'no_evidence';
}
/** The Couldn't-check code of a source's newest check (a pass that aged out is `stale`). */
function controlReason(c, state) {
    if (state === 'stale')
        return 'stale';
    if (isCouldntCheckReason(c.reason_code))
        return c.reason_code;
    let d = {};
    try {
        d = c.details_json ? JSON.parse(c.details_json) : {};
    }
    catch { /* derive from the status */ }
    return defaultCouldntCheckReason(c.status, d) ?? 'collector_error';
}
/** Worst-wins ordering when several sources evidence one control. */
const SEVERITY = {
    not_applicable: 0, pass: 1, excepted: 2, insufficient: 3, no_evidence: 4, couldnt_check: 5, stale: 6, fail: 7,
};
export async function computeControlStates(db, workspaceId, controlIds, opts = {}) {
    const out = new Map();
    if (controlIds.length === 0)
        return out;
    const now = (opts.now ?? new Date()).getTime();
    const ids = [...new Set(controlIds)];
    // Tenant plane (may be Postgres): chunked by control id under D1's 100-parameter
    // limit. The loop below keys on (control, connector), so a chunk boundary never
    // splits a key and each chunk's newest-first order is all it needs.
    const checks = await queryInChunks(db, ids, (ph, chunk) => ({
        sql: `SELECT c.check_id, c.control_id, c.connector_id, c.system_id, c.component_id, c.status, c.observed_at, c.reason_code, c.details_json, c.test_key
       FROM control_checks c
      WHERE c.workspace_id = ?
        AND c.control_id IN (${ph})
        AND ${countedResultSql('c')}
        ${opts.systemId ? 'AND (c.system_id = ? OR c.system_id IS NULL)' : ''}
      ORDER BY c.observed_at DESC`,
        params: opts.systemId ? [workspaceId, ...chunk, opts.systemId] : [workspaceId, ...chunk],
    }));
    const inIds = sqlIn(ids); // control_specs: control plane (always D1)
    const specs = await (opts.contentDb ?? db).query(`SELECT control_id, freshness_max_age_seconds FROM control_specs
      WHERE control_id IN ${inIds.sql} AND superseded_at IS NULL`, inIds.params);
    const freshness = new Map(specs.results.map((s) => [s.control_id, s.freshness_max_age_seconds]));
    const counted = opts.countedEnvironments
        ?? (opts.profileId ? await resolveCountedEnvironments(db, { workspaceId, profileId: opts.profileId }) : countedEnvironmentSet());
    const envOf = await connectorEnvironments(db, workspaceId);
    const conns = (await db.query(`SELECT connector_id, kind, status, last_run_at, config_json FROM evidence_connectors WHERE workspace_id = ?`, [workspaceId])).results;
    const kindOf = new Map(conns.map((r) => [r.connector_id, r.kind]));
    const statusOf = new Map(conns.map((r) => [r.connector_id, r.status]));
    // V109-1-19 (H8): when GAC Pro collects next (its last run + the effective cadence), for the
    // Couldn't-check rows GAC Pro retries on its own. R3 (R2-B1): only an active connection is
    // scheduled — a connection in error or turned off has no next collection.
    const nextRun = new Map(conns.map((r) => [r.connector_id, nextCollectionAt(r, now)]));
    const previewSeen = new Set();
    const previews = new Map();
    // Several connectors can evidence one control (Okta and Google both
    // report MFA), one connector can report several systems, and since
    // V113-0-3 one run can report several instances (one row per guardrail).
    // Take the newest check per (connector, system, instance) — an org-level
    // check (no system) and an aggregate (no instance) are their own sources —
    // then the worst of those: a control passes only when every source
    // currently passes (V102-0-3; AND over configuration items, D-113-18).
    const latestPerSource = new Map();
    for (const c of checks) {
        const sourceKey = `${c.control_id}\u0000${c.connector_id}\u0000${c.system_id ?? ''}\u0000${c.component_id ?? ''}`;
        if (latestPerSource.has(sourceKey) || previewSeen.has(sourceKey))
            continue; // newest first
        const env = envOf.get(c.connector_id) ?? 'production';
        let state;
        switch (c.status) {
            case 'pass': {
                // Standing specs (null freshness, D-85-14) stay fresh; a floor of one
                // day guards against clock skew on very short windows (isAgedOutPass, shared with Setup).
                state = isAgedOutPass(c.observed_at, freshness.get(c.control_id), now) ? 'stale' : 'pass';
                break;
            }
            case 'stale':
                state = 'stale';
                break;
            case 'skipped_exception':
                state = 'excepted';
                break;
            case 'insufficient_evidence':
                state = 'insufficient';
                break;
            case 'not_applicable':
                state = 'not_applicable';
                break;
            // V109-1-12 (LM-9): an error is Couldn't check, never a fail.
            case 'error':
                state = 'couldnt_check';
                break;
            default: state = 'fail';
        }
        const reason = isCouldntCheckState(state) ? controlReason(c, state) : undefined;
        if (!counted.has(env)) {
            // V108-3-5: an uncounted environment is a preview — shown, never a state.
            previewSeen.add(sourceKey);
            const list = previews.get(c.control_id) ?? [];
            list.push({ environment: env, state, last_check_id: c.check_id, observed_at: c.observed_at, reason: previewReason(env) });
            previews.set(c.control_id, list);
            continue;
        }
        latestPerSource.set(sourceKey, {
            control_id: c.control_id, state, ...(reason ? { reason_code: reason } : {}), last_check_id: c.check_id, observed_at: c.observed_at,
            ...(reason ? { couldnt_check_sources: [{
                        reason_code: reason, connector_id: c.connector_id, connector_kind: kindOf.get(c.connector_id) ?? null, system_id: c.system_id,
                        next_collection_at: nextRun.get(c.connector_id) ?? null, connector_status: (statusOf.get(c.connector_id) ?? null),
                        test_key: c.test_key ?? null,
                    }] } : {}),
        });
    }
    // V113-4-4 (ADR-0100 §4, §6): where a control has CI bindings, each CI-in-system is one cell judged by
    // the AI machine judgment over its binding (active, fresh, method verified, CI present, last check
    // passing) — the raw checks of a bound CI no longer count on their own (no double count), and an
    // unbound / stale / unverified cell is no_evidence (unmonitored), never a pass. Unbound CIs and
    // workspace-level checks keep today's path.
    const cells = await monitoringCells(db, workspaceId, ids, { systemId: opts.systemId, now: new Date(now), envOf, counted, content: opts.contentDb });
    const monitored = new Map();
    if (cells.length) {
        const bound = new Set(cells.map((c) => `${c.control_id}\u0000${c.component_id}`));
        for (const [key, st] of [...latestPerSource]) {
            const component = key.split('\u0000')[3] ?? '';
            if (component && bound.has(`${st.control_id}\u0000${component}`))
                latestPerSource.delete(key);
        }
        for (const c of cells) {
            // D-113-28: a CI still in its review window (or out of scope) is not a cell; pending ones are counted.
            if (c.review !== 'counted') {
                const m = monitored.get(c.control_id) ?? { cells: 0, unmonitored: 0 };
                if (c.review === 'pending_review')
                    m.pending_review = (m.pending_review ?? 0) + 1;
                // Review H2: an out-of-scope item is stated (who, when, failing) — the pack carries it, never silently dropped.
                else if (c.out_of_scope)
                    m.out_of_scope = [...(m.out_of_scope ?? []), c.out_of_scope];
                monitored.set(c.control_id, m);
                continue;
            }
            latestPerSource.set(`binding\u0000${c.binding_id}`, {
                control_id: c.control_id, state: c.state, last_check_id: c.check_id, observed_at: c.observed_at, basis: 'monitoring',
            });
            const m = monitored.get(c.control_id) ?? { cells: 0, unmonitored: 0 };
            m.cells++;
            if (c.state !== 'pass' && c.state !== 'fail')
                m.unmonitored++;
            monitored.set(c.control_id, m);
        }
    }
    // V109-1-19 (B1/H4): every source that couldn't be checked, with its connection, so each reason
    // is shown and its fix link opens that source's step (not only the worst source's).
    const couldntSources = new Map();
    for (const s of latestPerSource.values()) {
        for (const src of s.couldnt_check_sources ?? [])
            couldntSources.set(s.control_id, [...(couldntSources.get(s.control_id) ?? []), src]);
    }
    for (const s of latestPerSource.values()) {
        const current = out.get(s.control_id);
        if (!current || SEVERITY[s.state] > SEVERITY[current.state])
            out.set(s.control_id, s);
    }
    for (const [id, srcs] of couldntSources) {
        const cur = out.get(id);
        if (cur && isCouldntCheckState(cur.state))
            out.set(id, { ...cur, couldnt_check_sources: srcs });
        else if (cur) {
            const { couldnt_check_sources: _drop, ...rest } = cur;
            out.set(id, rest);
        }
    }
    for (const id of ids) {
        if (!out.has(id))
            out.set(id, { control_id: id, state: 'no_evidence', last_check_id: null, observed_at: null });
    }
    for (const [id, p] of previews) {
        const cur = out.get(id);
        if (cur)
            out.set(id, { ...cur, preview: p });
    }
    for (const [id, m] of monitored) {
        const cur = out.get(id);
        if (cur)
            out.set(id, { ...cur, basis: 'monitoring', monitoring: m });
    }
    // V117-3-3 / V117-3-3a (ADR-0112 §1, §5): a pass is judged on its evidence content where the spec declares content
    // checks, and on the floor fixes switched on for the program in view (default OFF).
    let programKeys = opts.programKeys;
    if (!programKeys && opts.profileId) {
        try {
            const p = await db.query(`SELECT program FROM certification_profiles WHERE workspace_id = ? AND profile_id = ?`, [workspaceId, opts.profileId]);
            programKeys = p.results.map((r) => r.program);
        }
        catch {
            programKeys = [];
        }
    }
    const countedChecks = checks.filter((c) => counted.has(envOf.get(c.connector_id) ?? 'production'));
    await applyEvidenceJudgment(db, opts.contentDb ?? db, workspaceId, out, { systemId: opts.systemId, now: new Date(now), programKeys, checks: countedChecks, forceFixes: opts.floorFixes });
    return out;
}
/**
 * The CI-in-system cells of the given controls (V113-4-4): one per component binding of this workspace's
 * systems (optionally one system), judged by `judgeBinding` — the judgment the AI machine rows record.
 * A cell whose last check comes from an uncounted environment is left out (a preview, V108-3-5).
 */
async function monitoringCells(db, workspaceId, controlIds, opts) {
    const bindings = await queryInChunks(db, [...controlIds], (ph, chunk) => ({
        sql: `SELECT b.* FROM monitoring_bindings b JOIN ai_systems s ON s.system_id = b.system_id LEFT JOIN customer_profiles cp ON cp.profile_id = s.profile_id
           WHERE COALESCE(s.workspace_id, cp.workspace_id) = ? AND b.component_id IS NOT NULL AND b.control_id IN (${ph})${opts.systemId ? ' AND b.system_id = ?' : ''}
           ORDER BY b.binding_id`,
        params: opts.systemId ? [workspaceId, ...chunk, opts.systemId] : [workspaceId, ...chunk],
    }));
    if (!bindings.length)
        return [];
    const checkIds = [...new Set(bindings.map((b) => b.last_check_id).filter((x) => !!x))];
    const checks = new Map((await queryInChunks(db, checkIds, (ph, chunk) => ({
        sql: `SELECT check_id, connector_id, status, observed_at, check_key, test_key, component_id FROM control_checks WHERE workspace_id = ? AND check_id IN (${ph})`,
        params: [workspaceId, ...chunk],
    }))).map((c) => [c.check_id, c]));
    const methodIds = [...new Set(bindings.map((b) => b.method_id).filter((x) => !!x))];
    const methods = new Map((await queryInChunks(db, methodIds, (ph, chunk) => ({
        sql: `SELECT * FROM monitoring_methods WHERE workspace_id = ? AND method_id IN (${ph})`, params: [workspaceId, ...chunk],
    }))).map((m) => [m.method_id, m]));
    const componentIds = [...new Set(bindings.map((b) => b.component_id))];
    const components = new Map((await queryInChunks(db, componentIds, (ph, chunk) => ({
        sql: `SELECT component_id, gone_at, retired_at, merged_into_id FROM ai_components WHERE workspace_id = ? AND component_id IN (${ph})`, params: [workspaceId, ...chunk],
    }))).map((c) => [c.component_id, c]));
    // Review M3: the same window as every other reader (the content handle).
    const review = new Map([...(await ciAssetReviewStatus(db, workspaceId, { now: opts.now, content: opts.content })).values()].map((s) => [`${s.system_id}\u0000${s.component_id}`, s]));
    const out = [];
    for (const b of bindings) {
        const check = b.last_check_id ? checks.get(b.last_check_id) ?? null : null;
        if (check && !opts.counted.has(opts.envOf.get(check.connector_id) ?? 'production'))
            continue;
        const comp = components.get(b.component_id);
        const j = judgeBinding({
            binding: b, method: b.method_id ? methods.get(b.method_id) ?? null : null, check,
            component: comp && !comp.retired_at && !comp.merged_into_id ? { gone_at: comp.gone_at } : null,
        }, opts.now);
        const state = j.outcome === 'pass' ? 'pass' : j.outcome === 'fail' ? 'fail' : j.reason === 'stale' ? 'stale' : 'no_evidence';
        out.push({ binding_id: b.binding_id, control_id: b.control_id, component_id: b.component_id, state, check_id: check?.check_id ?? null, observed_at: check?.observed_at ?? null, review: review.get(`${b.system_id}\u0000${b.component_id}`)?.status ?? 'counted',
            ...(review.get(`${b.system_id}\u0000${b.component_id}`)?.status === 'out_of_scope' ? { out_of_scope: outOfScopeItem(review.get(`${b.system_id}\u0000${b.component_id}`)) } : {}) });
    }
    return out;
}
function satisfied(state) {
    return state === 'pass' || state === 'not_applicable';
}
/** How a crediting control reads in a requirement's reason. */
function stateLabel(c) {
    if (c.state === 'couldnt_check')
        return `${c.control_id} (couldn't check: ${couldntCheckEntry(c.reason_code ?? 'collector_error').words})`;
    return `${c.control_id} (${c.state})`;
}
export function deriveRequirementStatus(crediting) {
    if (crediting.length === 0)
        return { status: 'unmapped', reason: 'No reviewed control credits this requirement yet.' };
    const applicable = crediting.filter((c) => c.state !== 'not_applicable');
    if (applicable.length === 0)
        return { status: 'met', reason: 'Every crediting control is not applicable.' };
    const passing = applicable.filter((c) => satisfied(c.state));
    if (passing.length === applicable.length) {
        return { status: 'met', reason: `Satisfied by ${passing.map((c) => c.control_id).join(', ')}.` };
    }
    if (passing.length > 0) {
        const missing = applicable.filter((c) => !satisfied(c.state)).map(stateLabel);
        return { status: 'partial', reason: `Still needed: ${missing.join(', ')}.` };
    }
    const states = [...new Set(crediting.map((c) => c.state))];
    return {
        status: 'gap',
        reason: states.length === 1 && states[0] === 'no_evidence'
            ? 'No evidence collected yet for the controls that satisfy this requirement.'
            : `Controls not passing: ${crediting.map(stateLabel).join(', ')}.`,
    };
}
export async function computeFrameworkReadiness(db, workspaceId, frameworkId, opts = {}) {
    const cls = opts.certificationClass ?? null;
    const content = opts.contentDb ?? db;
    const reqs = await content.query(`SELECT requirement_id, ref, title, group_ref, group_name, applicability_json FROM framework_requirements
      WHERE framework_id = ? AND status = 'active' ORDER BY group_ref, ref`, [frameworkId]);
    const inScope = reqs.results.filter((r) => appliesToClass(r.applicability_json, cls, opts.includeOptional ?? false));
    const edges = await content.query(`SELECT e.from_control_id, e.to_control_id, e.relation
       FROM control_edges e
       JOIN framework_requirements r ON r.requirement_id = e.to_control_id
      WHERE r.framework_id = ?
        AND e.relation IN ('subsumes', 'equivalent', 'partial')
        AND e.reviewer IS NOT NULL`, [frameworkId]);
    const byReq = new Map();
    for (const e of edges.results) {
        const list = byReq.get(e.to_control_id) ?? [];
        list.push({ control_id: e.from_control_id, relation: e.relation });
        byReq.set(e.to_control_id, list);
    }
    const counted = opts.countedEnvironments
        ?? (opts.profileId ? await resolveCountedEnvironments(db, { workspaceId, profileId: opts.profileId }) : countedEnvironmentSet());
    const states = await computeControlStates(db, workspaceId, edges.results.map((e) => e.from_control_id), { systemId: opts.systemId, now: opts.now, contentDb: content, profileId: opts.profileId, countedEnvironments: counted });
    const acceptances = await openAcceptancesByItem(db, workspaceId, opts.now ?? new Date());
    const nowMs = (opts.now ?? new Date()).getTime();
    // V109-5-5: responsibility per requirement from the program's live control applicability (not superseded, not rejected).
    const responsibility = new Map();
    if (opts.profileId) {
        const rows = await db.query(`SELECT requirement_id, responsibility FROM control_applicability
        WHERE workspace_id = ? AND profile_id = ? AND superseded_at IS NULL AND review_state <> 'rejected'`, [workspaceId, opts.profileId]).catch(() => ({ results: [] }));
        for (const r of rows.results)
            (responsibility.get(r.requirement_id) ?? responsibility.set(r.requirement_id, new Set()).get(r.requirement_id)).add(r.responsibility);
    }
    const requirements = inScope.map((r) => {
        const crediting = (byReq.get(r.requirement_id) ?? []).map((c) => {
            const s = states.get(c.control_id);
            return {
                ...c,
                state: s?.state ?? 'no_evidence',
                evidence_mode: EVIDENCE_MODE.get(c.control_id) ?? null,
                ...(s?.reason_code ? { reason_code: s.reason_code } : {}),
            };
        });
        const { status, reason } = deriveRequirementStatus(crediting);
        const check_state = requirementCheckState(status, crediting);
        const couldnt_check_reasons = check_state === 'couldnt_check'
            ? [...new Set(crediting.filter((c) => isCouldntCheckState(c.state)).map((c) => c.reason_code ?? 'collector_error'))].sort()
            : [];
        // V109-1-19: reason × source, counting the crediting controls each leaves unverified.
        const bySource = new Map();
        if (check_state === 'couldnt_check') {
            for (const c of crediting.filter((x) => isCouldntCheckState(x.state))) {
                const srcs = states.get(c.control_id)?.couldnt_check_sources?.length
                    ? states.get(c.control_id).couldnt_check_sources
                    : [{ reason_code: c.reason_code ?? 'collector_error', connector_id: null, connector_kind: null, system_id: null }];
                for (const s of srcs) {
                    const k = `${s.reason_code}\u0000${s.connector_id ?? ''}\u0000${s.test_key ?? ''}`;
                    const cur = bySource.get(k);
                    if (cur)
                        cur.controls++;
                    else
                        bySource.set(k, { ...s, controls: 1 });
                }
            }
        }
        const couldnt_check_sources = [...bySource.values()].sort((a, b) => b.controls - a.controls || (a.reason_code < b.reason_code ? -1 : a.reason_code > b.reason_code ? 1 : 0));
        const acc = acceptances.get(r.requirement_id);
        // V109-5-5: evidence age = the oldest newest-reading among its applicable crediting controls.
        const readings = crediting.filter((c) => c.state !== 'not_applicable').map((c) => states.get(c.control_id)?.observed_at).filter((x) => !!x);
        const oldest = readings.length ? Math.min(...readings.map((x) => Date.parse(x)).filter((x) => Number.isFinite(x))) : NaN;
        const resp = responsibility.get(r.requirement_id);
        return {
            requirement_id: r.requirement_id, ref: r.ref, title: r.title, group_ref: r.group_ref, group_name: r.group_name, status, reason, check_state, couldnt_check_reasons, couldnt_check_sources,
            ...(acc?.length ? { risk_acceptances: acc } : {}), crediting_controls: crediting,
            stale_met: check_state === 'couldnt_check' && couldnt_check_reasons.length > 0 && couldnt_check_reasons.every((x) => x === 'stale'),
            evidence_age_days: Number.isFinite(oldest) ? Math.max(0, Math.floor((nowMs - oldest) / 86_400_000)) : null,
            responsibility: resp ? (resp.size === 1 ? [...resp][0] : 'mixed') : null,
        };
    });
    const counts = { met: 0, partial: 0, gap: 0, unmapped: 0, total: requirements.length };
    const check_counts = { ...Object.fromEntries(REQUIREMENT_CHECK_STATES.map((s) => [s, 0])), total: requirements.length };
    const couldnt_check_reasons = {};
    const previewEnvs = new Set();
    for (const st of states.values())
        for (const p of st.preview ?? [])
            previewEnvs.add(p.environment);
    for (const r of requirements) {
        counts[r.status]++;
        check_counts[r.check_state]++;
        for (const code of r.couldnt_check_reasons)
            couldnt_check_reasons[code] = (couldnt_check_reasons[code] ?? 0) + 1;
    }
    const pct = (n) => (counts.total === 0 ? 0 : Math.round((n / counts.total) * 1000) / 10);
    const staleMet = requirements.filter((r) => r.stale_met).length;
    // V109-5-5: the stamps — the workspace's latest closed sweep (and its pinned catalog), the baseline, the counted scope.
    const run = (await db.query(`SELECT run_id, finished_at, catalog_version FROM scan_runs WHERE workspace_id = ? AND kind = 'sweep' AND state <> 'running' ORDER BY started_at DESC, run_id DESC LIMIT 1`, [workspaceId]).catch(() => ({ results: [] }))).results[0] ?? null;
    return {
        framework_id: frameworkId,
        certification_class: cls,
        counts,
        check_counts,
        quality: { stale_met: staleMet, indeterminate: check_counts.couldnt_check - staleMet },
        stamps: {
            run_id: run?.run_id ?? null, run_finished_at: run?.finished_at ?? null, catalog_version: run?.catalog_version ?? null,
            baseline: { framework_id: frameworkId, certification_class: cls }, counted_environments: [...counted].sort(),
        },
        couldnt_check_reasons,
        readiness_pct: pct(counts.met),
        coverage_pct: pct(counts.total - counts.unmapped),
        requirements,
        computed_at: (opts.now ?? new Date()).toISOString(),
        ...(previewEnvs.size ? { preview_environments: [...previewEnvs].sort() } : {}),
    };
}
//# sourceMappingURL=requirement-readiness-service.js.map